import { gql, NetworkStatus, useQuery } from "@apollo/client";
import debounce from "lodash/debounce";
import { useCallback, useState } from "react";

import { HISTORY_FILTER_LIMIT } from "@zapier/reporting/src/constants";
import { useSwrQuery } from "@zapier/reporting/src/hooks";
import {
  AppResultFragment,
  AppsByNameSearchQuery,
  AppsWithCurrentImplementationIdQuery,
} from "./useAppTypeahead.generated";

type Input = {
  ids: string[];
  limit?: number;
};

const appFragment = gql`
  fragment AppResult on App {
    currentImplementation {
      id
    }
    id
    images {
      url16X16
      url32X32
      url64X64
      url128X128
    }
    name
    slug
  }
`;

const appsByCurrentImplementationIdQuery = gql`
  ${appFragment}

  query AppsWithCurrentImplementationId($ids: [ID!]!) {
    appsByCurrentImplementationId(ids: $ids) {
      ...AppResult
    }
  }
`;

export const publicAppSearchQuery = gql`
  ${appFragment}

  query AppsByNameSearch(
    $excludeUpcoming: Boolean
    $limit: Int!
    $nameContains: String!
    $offset: Int
  ) {
    getAppsByNameContains(
      excludeUpcoming: $excludeUpcoming
      limit: $limit
      nameContains: $nameContains
      offset: $offset
    ) {
      edges {
        ...AppResult
      }
      totalCount
    }
  }
`;

export function useAppTypeahead({ ids, limit }: Input) {
  const [search, setSearch] = useState("");

  const searchResult = useSwrQuery<AppsByNameSearchQuery>(
    publicAppSearchQuery,
    {
      notifyOnNetworkStatusChange: true,
      variables: {
        excludeUpcoming: true,
        limit: limit || HISTORY_FILTER_LIMIT,
        nameContains: search,
      },
    }
  );

  const selectedAppResult = useQuery<AppsWithCurrentImplementationIdQuery>(
    appsByCurrentImplementationIdQuery,
    {
      skip: ids.length === 0,
      variables: {
        ids,
      },
    }
  );
  // Grandfathered in on 26-08-2024, OB-1373
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const onSearch = useCallback(
    debounce((inputValue) => {
      return setSearch(inputValue || "");
    }, 300),
    []
  );

  const apps = searchResult?.data?.getAppsByNameContains?.edges || [];

  function getSelectedAppsCache() {
    const serializedCache: Record<string, any> =
      selectedAppResult.client.cache.extract();

    return Object.values(serializedCache)
      .map((item) => {
        const id = item.currentImplementation?.__ref?.split(":")?.[1];
        if (item.__typename === "App" && ids.includes(id)) {
          return {
            ...item,
            currentImplementation: {
              ...item.currentImplementation,
              id,
            },
          };
        }
        return null;
      })
      .filter(Boolean) as AppResultFragment[];
  }

  function onLoadMore() {
    searchResult.fetchMore({
      updateQuery(prev, { fetchMoreResult }) {
        // We didn't get anything so nothing to update
        if (!fetchMoreResult) return prev;

        // Update the cache
        return {
          ...fetchMoreResult,
          getAppsByNameContains: {
            ...fetchMoreResult?.getAppsByNameContains,
            edges: [
              ...(prev.getAppsByNameContains?.edges || []),
              ...(fetchMoreResult?.getAppsByNameContains?.edges || []),
            ],
            totalCount: fetchMoreResult?.getAppsByNameContains?.totalCount ?? 0,
          },
        };
      },
      variables: {
        excludeUpcoming: true,
        limit: limit || HISTORY_FILTER_LIMIT,
        offset: apps.length,
        nameContains: search,
      },
    });
  }

  const selectedApps = selectedAppResult.loading
    ? getSelectedAppsCache()
    : selectedAppResult.data?.appsByCurrentImplementationId || [];

  return {
    apps,
    isAppLoading: selectedAppResult.loading,
    isLoadingMore: searchResult.networkStatus === NetworkStatus.fetchMore,
    isSearchLoading: searchResult.loading,
    onLoadMore:
      apps.length < (searchResult.data?.getAppsByNameContains?.totalCount || 0)
        ? onLoadMore
        : undefined,
    onSearch,
    selectedApps,
  };
}
